import { type RefObject, useLayoutEffect, useState } from "react";

/**
 * Whether the text in `ref` overflows its box, i.e. an ellipsis is showing: wider than a single-line
 * box, or taller than a line-clamped one.
 *
 * Re-measured when the text changes, when the box resizes, and once web fonts finish loading: a
 * fallback font changes the text width without changing the element's size, so a one-shot
 * measurement on mount can be stale. While `frozen`, the last answer is kept, for callers that let
 * the text wrap and still want the control that collapses it again.
 */
export function useIsTextClipped(ref: RefObject<HTMLElement | null>, { frozen = false } = {}): boolean {
  const [isClipped, setIsClipped] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (frozen || !element) return;
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      // A tight line-height lets glyphs poke a few pixels past the line box, which counts as vertical
      // overflow even when nothing is hidden. A hidden line adds a whole line, so ignore under half of one.
      const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight);
      const slack = Number.isNaN(lineHeight) ? 0 : lineHeight / 2;
      setIsClipped(element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight + slack);
    };
    measure();
    void document.fonts?.ready.then(measure);
    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    resizeObserver?.observe(element);
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(element, { childList: true, characterData: true, subtree: true });
    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, [ref, frozen]);

  return isClipped;
}
